import React, { useState, useEffect } from 'react';
import { User } from '../types/api';
import { userAPI } from '../services';
import Modal from './Modal';

interface MemberEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: User | null;
  onSaveSuccess?: () => void;
}

const MemberEditModal: React.FC<MemberEditModalProps> = ({
  isOpen,
  onClose,
  member,
  onSaveSuccess
}) => {
  const [editData, setEditData] = useState({
    lessonDayOfWeek: '',
    lessonStartTime: '',
    lessonEndTime: '',
    monthlyFee: '',
    paymentDayOfMonth: ''
  });

  useEffect(() => {
    if (member) {
      setEditData({
        lessonDayOfWeek: member.lessonDayOfWeek || '',
        lessonStartTime: member.lessonStartTime?.slice(0, 5) || '',
        lessonEndTime: member.lessonEndTime?.slice(0, 5) || '',
        monthlyFee: member.monthlyFee?.toString() || '',
        paymentDayOfMonth: member.paymentDayOfMonth?.toString() || ''
      });
    }
  }, [member]);

  const handleResetPassword = async () => {
    if (!member) return;
    const isConfirmed = window.confirm(
      `"${member.name}" 회원의 비밀번호를 연락처(${member.phoneNumber})로 초기화하시겠습니까?`
    );
    if (!isConfirmed) return;

    try {
      const response = await userAPI.resetUserPassword(member.userId);
      if (response.success) {
        alert(response.message || '비밀번호가 초기화되었습니다.');
      } else {
        alert(response.message || '비밀번호 초기화에 실패했습니다.');
      }
    } catch {
      alert('비밀번호 초기화 중 오류가 발생했습니다.');
    }
  };

  const handleSave = async () => {
    if (!member) return;

    if (!editData.lessonDayOfWeek || !editData.lessonStartTime || !editData.lessonEndTime) {
      alert('요일과 수업시간을 입력해주세요.');
      return;
    }

    try {
      const response = await userAPI.updateUser(member.userId, {
        lessonDayOfWeek: editData.lessonDayOfWeek as any,
        lessonStartTime: editData.lessonStartTime,
        lessonEndTime: editData.lessonEndTime,
        monthlyFee: editData.monthlyFee ? Number(editData.monthlyFee) : undefined,
        paymentDayOfMonth: editData.paymentDayOfMonth ? Number(editData.paymentDayOfMonth) : undefined
      });

      if (response.success) {
        alert('회원 정보가 수정되었습니다.');
        onClose();
        onSaveSuccess?.();
      } else {
        alert(response.message || '수정에 실패했습니다.');
      }
    } catch {
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${member?.name} 회원 정보 수정`}
      size="sm"
    >
      <div className="user-edit-form">
        <div className="edit-form-row">
          <label>요일</label>
          <select
            value={editData.lessonDayOfWeek}
            onChange={(e) => setEditData({ ...editData, lessonDayOfWeek: e.target.value })}
          >
            <option value="">선택</option>
            <option value="monday">월</option>
            <option value="tuesday">화</option>
            <option value="wednesday">수</option>
            <option value="thursday">목</option>
            <option value="friday">금</option>
            <option value="saturday">토</option>
            <option value="sunday">일</option>
          </select>
        </div>

        <div className="edit-form-row two-col">
          <div className="form-col">
            <label>시작</label>
            <input
              type="time"
              value={editData.lessonStartTime}
              onChange={(e) => setEditData({ ...editData, lessonStartTime: e.target.value })}
            />
          </div>
          <div className="form-col">
            <label>종료</label>
            <input
              type="time"
              value={editData.lessonEndTime}
              onChange={(e) => setEditData({ ...editData, lessonEndTime: e.target.value })}
            />
          </div>
        </div>

        <div className="edit-form-row two-col">
          <div className="form-col">
            <label>수업료</label>
            <input
              type="number"
              step="10000"
              min="0"
              placeholder="300000"
              value={editData.monthlyFee}
              onChange={(e) => setEditData({ ...editData, monthlyFee: e.target.value })}
            />
          </div>
          <div className="form-col">
            <label>납부일</label>
            <select
              value={editData.paymentDayOfMonth}
              onChange={(e) => setEditData({ ...editData, paymentDayOfMonth: e.target.value })}
            >
              <option value="">선택</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}일</option>
              ))}
            </select>
          </div>
        </div>

        <div className="edit-form-actions">
          <button
            className="btn btn-secondary"
            onClick={handleResetPassword}
          >
            비번초기화
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            취소
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MemberEditModal;
